import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { CopyButton } from "@/core/components/ui/copy-button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { AmountMinor } from "@/features/shared/components/amount-minor";
import type { TransactionDisputeDto } from "@/features/wallet/api/dto";
import {
  TRANSACTION_DISPUTE_STATUSES,
  type TransactionDisputeStatus,
} from "@/features/wallet/api/enums";
import { DisputeRowActions } from "@/features/wallet/components/disputes/dispute-row-actions";

/** Ligne de la liste : `DataTableClar` exige un `id` requis. */
export type TransactionDisputeRow = TransactionDisputeDto & { id: string };

const STATUS_VARIANTS: Record<
  TransactionDisputeStatus,
  "warning" | "success" | "secondary"
> = {
  OPEN: "warning",
  REVERSED: "success",
  REJECTED: "secondary",
};

const statusLabel = (status: string) => t(`wallet.disputes.status.${status}`);

/**
 * Colonnes de la file des contestations (`POST /TransactionDisputes/all`,
 * guide v3.1). `TransactionDisputeFilter` : `status`, `transactionId`
 * uniquement — les autres colonnes n'ont pas de champ API, d'où `denied`.
 * Montants et identifiants en `NoTranslate` (règle notranslate).
 */
export const disputesColumns: ColumnsClarFactory<TransactionDisputeRow> = (
  navigate
) => [
  {
    id: "transactionReference",
    accessorKey: "transactionReference",
    header: t("wallet.disputes.columns.transactionReference"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs font-medium">
        {(getValue() as string | undefined) || "—"}
      </NoTranslate>
    ),
    // Pas dans TransactionDisputeFilter (Postman : status / transactionId).
    meta: { type: "denied" },
  },
  {
    id: "transactionId",
    accessorKey: "transactionId",
    header: t("wallet.disputes.columns.transactionId"),
    cell: ({ getValue }) => {
      const transactionId = (getValue() as string | undefined)?.trim();
      if (!transactionId) return "—";
      return (
        <span className="inline-flex items-center gap-1">
          <NoTranslate as="span" className="font-mono text-xs">
            {transactionId}
          </NoTranslate>
          <CopyButton value={transactionId} />
        </span>
      );
    },
    meta: { type: "text" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("wallet.disputes.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as TransactionDisputeStatus | undefined;
      return status ? (
        <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>
          {statusLabel(status)}
        </Badge>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: TRANSACTION_DISPUTE_STATUSES.map((value) => ({
        value,
        label: statusLabel(value),
      })),
    },
  },
  {
    id: "contestedAmountMinor",
    accessorKey: "contestedAmountMinor",
    header: t("wallet.disputes.columns.contestedAmount"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.contestedAmountMinor}
        currency={row.original.currency}
        className="font-medium"
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "heldAmountMinor",
    accessorKey: "heldAmountMinor",
    header: t("wallet.disputes.columns.heldAmount"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.heldAmountMinor}
        currency={row.original.currency}
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "openedAt",
    accessorKey: "openedAt",
    header: t("wallet.disputes.columns.openedAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "expiresAt",
    accessorKey: "expiresAt",
    header: t("wallet.disputes.columns.expiresAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <DisputeRowActions dispute={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
